import type {IInputItem} from '../types/IInputItem';
import type {IBookingInfoItem} from '../types/IBookingInfoItem';
import type {TranslationFunction} from 'src/types/language-support.ts';
import type {IBookingMachineSnapshot} from '../machines/bookingMachine';
import capitalize from 'lodash/capitalize';

import {
  getWeekDates,
  canNavigateNextWeek,
  hasSlotsInNormalRange,
  getMeetLinkProviders,
  getLocationTitleLabelMap,
  canNavigatePreviousWeek,
  getSelectedStartDateOfWeekFromSequenceDays,
} from '../utils/booking';

import {
  timezoneName,
  parseTime,
  formatISO,
  addDays,
  createTZDate,
  displayDate,
  displayTime,
  displayDateWithShortFormat,
  displayWeekInShortFormat,
  displayDateWithOrdinal,
  wrangleMoment,
} from '../utils/date';
import type {IEventQuestion} from '../types/IEventQuestion';
import type {IEvent} from '../types/IEvent';
import type {ITimeSlots} from '../types/ITimeSlots';

// Helper functions to get data from sequenceDays based on sequenceNumber
const getSequenceDayBySequenceNumber = (
  sequenceDays: IBookingMachineSnapshot['context']['sequenceDays'],
  sequenceNumber: number,
) => {
  return sequenceDays?.find((day) => day.sequenceNumber === sequenceNumber);
};

export const getSelectedDateFromSequenceDays = (
  sequenceDays: IBookingMachineSnapshot['context']['sequenceDays'],
  sequenceNumber: number,
): string | undefined => {
  return getSequenceDayBySequenceNumber(sequenceDays, sequenceNumber)
    ?.selectedDate;
};

export const getSelectedTimeSlotFromSequenceDays = (
  sequenceDays: IBookingMachineSnapshot['context']['sequenceDays'],
  sequenceNumber: number,
) => {
  return getSequenceDayBySequenceNumber(sequenceDays, sequenceNumber)
    ?.selectedTimeSlot;
};

export const selectBookingInfo = (
  state: IBookingMachineSnapshot,
  t: TranslationFunction,
  language: string,
  makeKey: (key: string) => string,
) => {
  const meetLinkProviders = getMeetLinkProviders(t);
  const locationTitleLabelMap = getLocationTitleLabelMap(t);
  const bookingInfoItems: IBookingInfoItem[] = [];
  const eventDetails = state.context.eventDetails as IEvent;
  const isRescheduleFlow = Boolean(eventDetails?.id);

  if (!state.matches('bookingForm') && !isRescheduleFlow) {
    const durationUnit = state.context.eventTemplate?.duration.unit;
    const durationAmount = state.context.eventTemplate?.duration.amount;

    if (durationUnit && durationAmount) {
      bookingInfoItems.push({
        label: `${durationAmount} ${t(
          makeKey(capitalize(durationUnit)),
          capitalize(durationUnit),
        )} ${t('Meeting', 'Meeting')}`,
        icon: 'clock',
      });
    }
  }

  if (
    isRescheduleFlow &&
    eventDetails?.event_end &&
    eventDetails?.event_start
  ) {
    const startDate = wrangleMoment(eventDetails?.event_start);
    const endDate = wrangleMoment(eventDetails?.event_end);

    bookingInfoItems.push({
      label: displayDate(startDate, language),
      icon: 'calendar',
    });

    const selectDateWithTimezone = timezoneName(
      eventDetails?.event_start,
      state.context.language,
    );

    bookingInfoItems.push({
      label: `${displayTime(startDate, language)} - ${displayTime(
        endDate,
        language,
      )}`,
      subLabel: selectDateWithTimezone
        ? t(makeKey(selectDateWithTimezone), selectDateWithTimezone)
        : '',
      icon: 'clock',
    });
  }

  if (state.matches('bookingForm')) {
    const currentSequenceNumber = state.context.currentSequenceNumber ?? 0;
    const selectedDate = getSelectedDateFromSequenceDays(
      state.context.sequenceDays,
      currentSequenceNumber,
    );
    const selectedTimeSlot = getSelectedTimeSlotFromSequenceDays(
      state.context.sequenceDays,
      currentSequenceNumber,
    );

    if (selectedDate && selectedTimeSlot) {
      const selectDateWithTimezone = wrangleMoment(`${selectedDate}T00:00:00`);

      bookingInfoItems.push({
        label: displayDate(selectDateWithTimezone, language),
        icon: 'calendar',
      });

      const startTimeDate = parseTime(
        selectedTimeSlot.start_time,
        'HH:mm',
        new Date(),
      );

      const endTimeDate = parseTime(
        selectedTimeSlot.end_time,
        'HH:mm',
        new Date(),
      );

      const timezone = timezoneName(`${selectedDate}T00:00:00`, language) || '';

      bookingInfoItems.push({
        label: `${displayTime(startTimeDate, language)} - ${displayTime(
          endTimeDate,
          language,
        )}`,
        subLabel: timezone,
        icon: 'clock',
      });
    }
  }

  state.context.eventTemplate?.location_meta.forEach((value) => {
    const {label, icon} = locationTitleLabelMap[value.type];
    bookingInfoItems.push({
      label: meetLinkProviders[value.meta.provider] || label || '',
      icon,
    });
  });

  return {
    title: state.context.eventTemplate?.name || eventDetails?.title || '',
    bookingInfoItems,
  };
};

export const selectBookingFormInputs = (
  state: IBookingMachineSnapshot,
  t: TranslationFunction,
): IInputItem[] => {
  const additionalQuestions =
    state.context.eventTemplate?.additional_questions?.map(
      (question, index): IInputItem => {
        return {
          name: `additionalQuestion-${index}`,
          label: question.question,
          type: question.answer_type,
          options: question.choices,
        };
      },
    ) || [];

  return [
    {
      name: 'guestName',
      label: t('Full_Name', 'Full Name'),
      isRequired: true,
      placeholder: t('Enter_full_name', 'Enter full name'),
      type: 'text',
    },
    {
      name: 'guestEmail',
      label: t('Email', 'Email'),
      isRequired: true,
      placeholder: t('Enter_email', 'Enter email'),
      type: 'email',
    },
    {
      name: 'guestPhone',
      label: t('Phone_Number', 'Phone Number'),
      isRequired: true,
      placeholder: t('Enter_phone_number', 'Enter phone number'),
      type: 'phone',
    },
    ...additionalQuestions,
  ];
};

export const selectAdditionalQuestions = (
  state: IBookingMachineSnapshot,
): IEventQuestion[] => {
  return state.context.eventTemplate?.additional_questions || [];
};

// passing state here for future use
export const selectBookingNotifyHostInputs = (
  state: IBookingMachineSnapshot,
  t: TranslationFunction,
): IInputItem[] => {
  return [
    {
      name: 'guestName',
      label: t('Full_Name', 'Full Name'),
      isRequired: true,
      placeholder: t('Enter_full_name', 'Enter full name'),
      type: 'text',
    },
    {
      name: 'guestEmail',
      label: t('Email', 'Email'),
      isRequired: true,
      placeholder: t('Enter_email', 'Enter email'),
      type: 'email',
    },
    {
      name: 'guestPhone',
      label: t('Phone_Number', 'Phone Number'),
      isRequired: true,
      placeholder: t('Enter_phone_number', 'Enter phone number'),
      type: 'phone',
    },
  ];
};

export const selectCurrentDateSlots = (state: IBookingMachineSnapshot) => {
  const currentSequenceNumber = state.context.currentSequenceNumber ?? 0;
  const selectedDate = getSelectedDateFromSequenceDays(
    state.context.sequenceDays,
    currentSequenceNumber,
  );
  const selectedDay = getSequenceDayBySequenceNumber(
    state.context.sequenceDays,
    currentSequenceNumber,
  );

  if (selectedDate) {
    // Use timeSlots from selectedDay if available, fallback to global context
    const timeSlots = selectedDay?.timeSlots;
    return timeSlots?.free_time_slots?.[selectedDate] || [];
  }
  return [];
};

export const selectSelectedTimeSlot = (state: IBookingMachineSnapshot) => {
  const currentSequenceNumber = state.context.currentSequenceNumber ?? 0;
  return getSelectedTimeSlotFromSequenceDays(
    state.context.sequenceDays,
    currentSequenceNumber,
  );
};

export const selectCanNavigatePrevious = (
  state: IBookingMachineSnapshot,
): boolean => {
  const {sequenceDays, currentSequenceNumber, availabilityRange} =
    state.context;

  if (!availabilityRange) {
    throw new Error('Availability range is not found');
  }

  const sequenceNumber = currentSequenceNumber ?? 0;

  if (sequenceNumber > 0) {
    return false;
  }

  const selectedStartDateOfWeek = getSelectedStartDateOfWeekFromSequenceDays(
    sequenceDays,
    sequenceNumber,
  );

  if (!selectedStartDateOfWeek) {
    throw new Error('Selected start date of week is not found for current day');
  }

  if (state.matches({dateTimePicker: 'availabilityLoading'})) {
    return false;
  }
  const timezone = state.context.timezone;

  return canNavigatePreviousWeek(
    timezone,
    selectedStartDateOfWeek,
    availabilityRange,
  );
};

export const selectCanNavigateNext = (
  state: IBookingMachineSnapshot,
): boolean => {
  const {
    sequenceDays,
    currentSequenceNumber,
    availabilityRange,
    eventTemplate,
  } = state.context;

  if (!availabilityRange) {
    throw new Error('Availability range is not found');
  }

  const sequenceNumber = currentSequenceNumber ?? 0;
  const showExtendedAvailabilityByDefault =
    eventTemplate?.extended_future_limit?.policy === 'always';

  if (sequenceNumber > 0) {
    return false;
  }

  const selectedStartDateOfWeek = getSelectedStartDateOfWeekFromSequenceDays(
    sequenceDays,
    sequenceNumber,
  );

  if (!selectedStartDateOfWeek) {
    throw new Error('Selected start date of week is not found for current day');
  }

  if (state.matches({dateTimePicker: 'availabilityLoading'})) {
    return false;
  }

  const selectedDay = sequenceDays?.find(
    (day) => day.sequenceNumber === sequenceNumber,
  );
  const timeSlots = selectedDay?.timeSlots;
  const timezone = state.context.timezone;

  const hasSlotsInNormalRangeTillCurrentWeek = hasSlotsInNormalRange(
    timeSlots as ITimeSlots,
    availabilityRange.end_at,
    timezone,
  );

  return canNavigateNextWeek({
    timezone,
    selectedStartDateOfWeek,
    availabilityRange,
    hasNoSlotsInNormalRange: !hasSlotsInNormalRangeTillCurrentWeek,
    showExtendedAvailabilityByDefault,
  });
};

export const selectSelectedDate = (
  state: IBookingMachineSnapshot,
  _language: string,
) => {
  const currentSequenceNumber = state.context.currentSequenceNumber ?? 0;
  const selectedDate = getSelectedDateFromSequenceDays(
    state.context.sequenceDays,
    currentSequenceNumber,
  );
  if (!selectedDate) {
    return undefined;
  }

  return {
    key: selectedDate,
    label: selectedDate,
  };
};

export const selectWeekDays = (
  state: IBookingMachineSnapshot,
  language: string,
) => {
  const {sequenceDays, currentSequenceNumber} = state.context;

  const sequenceNumber = currentSequenceNumber ?? 0;
  const selectedStartDateOfWeek = getSelectedStartDateOfWeekFromSequenceDays(
    sequenceDays,
    sequenceNumber,
  );

  if (!selectedStartDateOfWeek) {
    throw new Error('Selected start date of week is not found for current day');
  }

  const weekDates = getWeekDates(selectedStartDateOfWeek);
  const weekDayOptions = weekDates.map((date) => ({
    key: formatISO(date, {representation: 'date'}),
    label: displayDateWithShortFormat(date, language),
    secondaryLabel: displayWeekInShortFormat(date, language),
  }));

  const selectedDay = sequenceDays?.find(
    (day) => day.sequenceNumber === sequenceNumber,
  );
  const timeSlots = selectedDay?.timeSlots;

  const allTimeSlotsKeys = Object.keys(timeSlots?.free_time_slots || {});
  const disabledWeekDays = weekDayOptions
    .filter((day) => !allTimeSlotsKeys.includes(day.key))
    .map((day) => day.key);

  return {
    weekDayOptions,
    disabledWeekDays,
  };
};

export const selectWeekRangeDisplay: (
  state: IBookingMachineSnapshot,
  language: string,
) => string = (state, language) => {
  const {sequenceDays, currentSequenceNumber} = state.context;

  const sequenceNumber = currentSequenceNumber ?? 0;
  const selectedStartDateOfWeek = getSelectedStartDateOfWeekFromSequenceDays(
    sequenceDays,
    sequenceNumber,
  );

  if (!selectedStartDateOfWeek) {
    throw new Error('Selected start date of week is not found for current day');
  }

  const weekStartDate = selectedStartDateOfWeek;
  const weekEndDate = addDays(weekStartDate, 6);

  return `${displayDate(weekStartDate, language)} - ${displayDate(
    weekEndDate,
    language,
  )}`;
};

export const selectIsRescheduleReasonValid = (
  state: IBookingMachineSnapshot,
) => {
  const reason = state.context.rescheduleReason;
  return Boolean(reason && reason !== '' && reason.trim().length > 0);
};

export const selectSequenceDays = (state: IBookingMachineSnapshot) => {
  return state.context.sequenceDays ?? [];
};

export const selectTotalDays = (state: IBookingMachineSnapshot): number => {
  const sequenceDays = selectSequenceDays(state);
  if (sequenceDays.length === 0) {
    return 1;
  }
  return sequenceDays.length;
};

export const selectIsMultiDay = (state: IBookingMachineSnapshot): boolean => {
  return selectTotalDays(state) > 1;
};

export const selectEventTemplate = (state: IBookingMachineSnapshot) => {
  return state.context.eventTemplate;
};

export const selectIsSequentialTemplate = (
  state: IBookingMachineSnapshot,
): boolean => {
  return (
    state.context.eventTemplate?.distribution_rule === 'sequential_interview'
  );
};

export const selectCurrentSequenceNumber = (
  state: IBookingMachineSnapshot,
): number => {
  return state.context.currentSequenceNumber ?? 0;
};

export const selectFormattedSelectedDateForDay = (
  state: IBookingMachineSnapshot,
  dayNumber: number,
  timezone: string,
  language: string,
): string | null => {
  // Map dayNumber to sequenceNumber (dayNumber starts from 1, sequenceNumber from 0)
  const sequenceNumber = dayNumber - 1;
  const selectedDay = state.context.sequenceDays?.find(
    (day) => day.sequenceNumber === sequenceNumber,
  );
  const selectedDate = selectedDay?.selectedDate;

  if (!selectedDate || !timezone) {
    return null;
  }

  try {
    const date = createTZDate(`${selectedDate}T00:00:00`, timezone);
    const formattedDate = displayDateWithOrdinal(date, language);
    return formattedDate;
  } catch {
    return null;
  }
};
